import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Bot, Loader2, Plus } from 'lucide-react';
import Select from 'react-select';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../Context/authContext';
import { TemporaryChatContext } from '../../Context/temporaryChatContext';
import agentService from '../../services/agentService';
import sessionsService from '../../services/sessionsServices';
import Toast from '../Toast';
import Button from '../Button';
import { ChatHeaderBar } from '../ui/ui.styles';
import {
  DEFAULT_AGENT,
  DEFAULT_AGENT_ID,
  findDefaultAgent,
  isDefaultAgent,
  toDefaultAwareAgentOption,
} from '../../constants/defaultAgent';

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 34,
    minWidth: 160,
    backgroundColor: 'var(--card)',
    borderColor: state.isFocused ? 'var(--o1)' : 'var(--line)',
    borderRadius: 9999,
    boxShadow: state.isFocused ? '0 0 0 3px var(--o-halo)' : 'none',
    fontSize: 14,
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    '&:hover': { borderColor: 'var(--o1)' },
  }),
  valueContainer: base => ({ ...base, padding: '0 8px' }),
  input: base => ({ ...base, color: 'var(--ink)', margin: 0, padding: 0 }),
  singleValue: base => ({ ...base, color: 'var(--ink)', margin: 0 }),
  placeholder: base => ({ ...base, color: 'var(--quiet)', margin: 0 }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: base => ({ ...base, color: 'var(--quiet)', padding: '0 8px' }),
  menu: base => ({
    ...base,
    backgroundColor: 'var(--card)',
    border: '1px solid var(--line)',
    borderRadius: 12,
    boxShadow: 'var(--shadow-lg)',
    overflow: 'hidden',
    zIndex: 50,
  }),
  menuList: base => ({ ...base, padding: 4, maxHeight: 240 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? 'var(--o1)'
      : state.isFocused
        ? 'var(--o-tint)'
        : 'transparent',
    color: 'var(--ink)',
    fontSize: 14,
    cursor: 'pointer',
    borderRadius: 8,
    padding: '8px 10px',
  }),
  groupHeading: base => ({
    ...base,
    color: 'var(--quiet)',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    padding: '8px 10px 4px',
  }),
};

function toOption(agent) {
  return toDefaultAwareAgentOption(agent);
}

function getIconSrc(agent) {
  if (!agent?.icon || typeof agent.icon !== 'string') return null;
  if (agent.icon.startsWith('http') || agent.icon.startsWith('data:') || agent.icon.startsWith('/')) {
    return agent.icon;
  }
  return `data:image/png;base64,${agent.icon}`;
}

function mergeAgentDetails(primary, fromList) {
  if (!primary?.value && !fromList?.value) return primary || fromList || null;
  return {
    label: primary?.label || fromList?.label || 'Agent',
    value: primary?.value || fromList?.value,
    icon: primary?.icon || fromList?.icon || null,
    mode: primary?.mode || fromList?.mode || 'agent',
  };
}

export default function KnowHowChatHeader() {
  const navigate = useNavigate();
  const { id: routeSessionId } = useParams();
  const queryClient = useQueryClient();
  const [switching, setSwitching] = useState(false);
  const { user } = useContextHook(AuthContext, v => ({ user: v.user }));
  const { sessionAgent, selectedAgent, updateAgent, setChatmessages, setSessionAgent, setSessionID } =
    useContext(TemporaryChatContext);

  const { data: agentsData, isLoading } = useQuery({
    queryKey: ['get-all-agents'],
    queryFn: () => agentService.getAllAgent(),
    staleTime: Infinity,
  });
  const { data: feedaData } = useQuery({
    queryKey: ['get-feeda-agents'],
    queryFn: () => agentService.getAllfeedaAgents(),
  });

  const userAgents = agentsData?.agents || [];
  const feedaAgents = feedaData?.agents || [];

  const defaultFromApi = useMemo(
    () => findDefaultAgent([...feedaAgents, ...userAgents]),
    [feedaAgents, userAgents],
  );
  const defaultOption = useMemo(
    () => (defaultFromApi ? toDefaultAwareAgentOption(defaultFromApi) : { ...DEFAULT_AGENT }),
    [defaultFromApi],
  );

  const agentOptions = useMemo(() => {
    const seen = new Set([String(defaultOption.value)]);
    const adminOptions = [
      defaultOption,
      ...feedaAgents.map(toOption).filter(o => o?.value && !seen.has(String(o.value)) && seen.add(String(o.value))),
    ];
    const userOptions = userAgents
      .map(toOption)
      .filter(o => o?.value && !seen.has(String(o.value)) && seen.add(String(o.value)));
    const groups = [];
    if (adminOptions.length) groups.push({ label: 'Official', options: adminOptions });
    if (userOptions.length) groups.push({ label: 'Your agents', options: userOptions });
    return groups;
  }, [defaultOption, feedaAgents, userAgents]);

  const flatOptions = useMemo(() => agentOptions.flatMap(g => g.options || []), [agentOptions]);

  useEffect(() => {
    if (routeSessionId) return;
    if (selectedAgent?.mode === 'team') return;
    if (selectedAgent?.value) return;
    updateAgent(defaultOption);
    setSessionAgent({ ...defaultOption });
  }, [routeSessionId, selectedAgent, defaultOption, updateAgent, setSessionAgent]);

  // Prefer exact API icons for the default agent only when not bound to another session agent.
  useEffect(() => {
    if (!defaultFromApi?.icon) return;
    if (selectedAgent?.mode === 'team') return;
    if (routeSessionId && sessionAgent?.value && !isDefaultAgent(sessionAgent)) return;

    if (isDefaultAgent(selectedAgent) && selectedAgent?.icon !== defaultOption.icon) {
      updateAgent(defaultOption);
    }
    if (isDefaultAgent(sessionAgent) && sessionAgent?.icon !== defaultOption.icon) {
      setSessionAgent({ ...defaultOption });
    }
  }, [defaultFromApi, defaultOption, routeSessionId, selectedAgent, sessionAgent, updateAgent, setSessionAgent]);

  // Keep header + selected agent aligned with the session's agent while viewing a chat.
  useEffect(() => {
    if (!routeSessionId || !sessionAgent?.value) return;
    if (selectedAgent?.mode === 'team') return;

    const fromList = flatOptions.find(option => String(option.value) === String(sessionAgent.value));
    const agentObj = mergeAgentDetails(sessionAgent, fromList);
    if (!agentObj?.value) return;

    const selectedNeedsUpdate =
      String(selectedAgent?.value) !== String(agentObj.value) ||
      selectedAgent?.icon !== agentObj.icon ||
      selectedAgent?.label !== agentObj.label;

    if (selectedNeedsUpdate) {
      updateAgent(agentObj);
    }

    if (
      fromList &&
      (sessionAgent?.icon !== agentObj.icon || sessionAgent?.label !== agentObj.label)
    ) {
      setSessionAgent(agentObj);
    }
  }, [routeSessionId, sessionAgent, flatOptions, selectedAgent, updateAgent, setSessionAgent]);

  const selectedOption = useMemo(() => {
    const currentValue = routeSessionId
      ? sessionAgent?.value || selectedAgent?.value || DEFAULT_AGENT_ID
      : selectedAgent?.value || sessionAgent?.value || DEFAULT_AGENT_ID;
    const found = flatOptions.find(option => String(option.value) === String(currentValue));
    if (found) return found;
    if (routeSessionId && sessionAgent?.value) return sessionAgent;
    return selectedAgent || defaultOption;
  }, [flatOptions, selectedAgent, sessionAgent, defaultOption, routeSessionId]);

  const displayAgent = useMemo(() => {
    if (routeSessionId && sessionAgent?.value) {
      const fromList = flatOptions.find(option => String(option.value) === String(sessionAgent.value));
      return mergeAgentDetails(sessionAgent, fromList) || selectedOption;
    }
    return selectedOption;
  }, [routeSessionId, sessionAgent, flatOptions, selectedOption]);

  const iconSrc = getIconSrc(displayAgent) || getIconSrc(defaultOption);

  const startSessionWith = async agentObj => {
    setSwitching(true);
    try {
      setChatmessages([]);
      updateAgent(agentObj);
      setSessionAgent(agentObj);
      const sessionRes = await sessionsService.createSession({
        user_id: user?.data?.ID,
        platform_id: 'knowhowlabs',
        agent_id: agentObj?.value,
        project_id: 'test',
        mode: agentObj?.mode,
      });
      setSessionID(sessionRes?.session_id);
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
      navigate(`/chat/${sessionRes?.session_id}`);
    } catch (error) {
      console.log(error);
      Toast({ type: 'error', message: 'Could not start a chat with this agent' });
    } finally {
      setSwitching(false);
    }
  };

  const handleNewChat = () => {
    const agent = selectedAgent?.value ? selectedAgent : sessionAgent;
    if (!agent?.value) {
      Toast({ type: 'info', message: 'Please select an agent first' });
      return;
    }
    startSessionWith(agent);
  };

  const handleAgentChange = option => {
    if (!option) return;
    const agentObj = {
      label: option.label,
      value: option.value,
      icon: option.icon,
      mode: option.mode || 'agent',
    };
    updateAgent(agentObj);
    setSessionAgent(agentObj);
    setChatmessages([]);
    setSessionID(null);
    navigate('/chat');
  };

  return (
    <ChatHeaderBar>
      <div className="agent-icon">{iconSrc ? <img src={iconSrc} alt="" /> : <Bot size={16} />}</div>
      <div className="agent-meta">
        <span className="agent-name">{displayAgent?.label || 'Noel KnowHow AIA Copilot'}</span>
        <span className="agent-sub">{routeSessionId ? 'Active session' : 'Chat'}</span>
      </div>
      <div className="actions">
        <Button width="auto" onClick={handleNewChat} disabled={switching || isLoading} loader={switching}>
          {switching ? <Loader2 size={16} /> : <Plus size={16} />} New Chat
        </Button>
        <Select
          options={agentOptions}
          value={selectedOption}
          onChange={handleAgentChange}
          isDisabled={switching}
          isSearchable
          placeholder="Assign agent"
          noOptionsMessage={() => 'No agents found'}
          styles={selectStyles}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          menuPosition="fixed"
        />
      </div>
    </ChatHeaderBar>
  );
}
